// utils/JsonConfigPopulator.js

import { deriveScriptFolderName, buildScriptProjectPath } from './scriptFolderName';

/** Replace any whitespace runs with underscores so names are safe for filenames and S3 paths. */
const sanitizeName = (name) => (name ? String(name).replace(/\s+/g, '_') : name);

class JsonConfigPopulator {
  
  /**
   * Populates a JSON configuration object with required project data
   * @param {Object} jsonConfig - The raw JSON configuration object
   * @param {Object} projectData - Created project data from API response
   * @param {Object} userData - User data containing tenantId
   * @param {Object} endpoint - Endpoint information
   * @param {Array} generatedFiles - Array of generated files (CSV, etc.)
   * @param {string} [folderNameOverride] - The folder name the caller will actually
   *   create the script folder with, including any dedup suffix. Pass it whenever
   *   the caller resolves the name itself; see the note at the derivation below.
   * @returns {Object} - Populated JSON configuration
   */
static populateConfig(jsonConfig, projectData, userData, endpoint, generatedFiles = [], folderNameOverride = '') {
  console.log('🚀 ========================================');
  console.log('🚀 JsonConfigPopulator.populateConfig CALLED');
  console.log('🚀 ========================================');

  if (!jsonConfig || !projectData || !userData || !endpoint) {
    throw new Error('Missing required parameters for JSON config population');
  }

  // Get required IDs
  const tenantId = userData.tenantId || userData.getOrganizationId?.() || '';
  const projectId = projectData.id || projectData.projectId || '';
  const projectName = projectData.name || '';

  // The folder name MUST be the one the caller actually creates the folder with.
  // When this was derived here independently it drifted from the creating helper
  // (this one did not uppercase, that one did), so projectPath and the dataSource
  // paths below named a folder that does not exist — and the editor's uploads then
  // conjured it as a phantom duplicate script (TSB-4519). Callers that resolve the
  // name up front, including the dedup `_1` suffix, pass it in; the fallback shares
  // the same derivation so the two can no longer disagree.
  // See shared/middleware/utils/scriptFolderName.js.
  const folderName = folderNameOverride || deriveScriptFolderName(endpoint, jsonConfig);

  // Clone the config to avoid mutating the original
  const populatedConfig = JSON.parse(JSON.stringify(jsonConfig));

  // Populate basic project information
  populatedConfig.tenantId = tenantId;
  populatedConfig.projectId = projectId;
  populatedConfig.projectName = projectName;
  populatedConfig.projectPath = buildScriptProjectPath(projectId, projectName, folderName);

  // Add stopOnFailure if not present
  if (!populatedConfig.stopOnFailure) {
    populatedConfig.stopOnFailure = "false";
  }

  // Derive a human-readable test name from the endpoint (e.g. "POST /users")
  const endpointMethodUpper = (endpoint.method || 'GET').toUpperCase();
  const endpointPath = endpoint.path || '';
  // Use operationId summary if available, otherwise "METHOD /path"
  const endpointLabel = endpoint.summary || `${endpointMethodUpper} ${endpointPath}`;
  const expectedProtocol = `HTTP_${endpointMethodUpper}`;

  // Fix tests: ensure test name and step methods come from the real endpoint
  if (populatedConfig.tests && Array.isArray(populatedConfig.tests)) {
    populatedConfig.tests = populatedConfig.tests.map(test => {
      // Fix test name if blank or looks like a code constant (e.g. "HTTP_METHOD_CONFIG.GET")
      const isCodeConstant = (name) => !name || /HTTP_METHOD_CONFIG|STEP_TYPE_CONFIG/i.test(name);
      const fixedName = isCodeConstant(test.name) ? endpointLabel : test.name;

      // Fix each step's method/protocol from the endpoint
      const fixedSteps = (test.steps || []).map(step => {
        const fixedStep = { ...step };
        // Fix step-level method field
        if (!fixedStep.method || /HTTP_METHOD_CONFIG/i.test(fixedStep.method)) {
          fixedStep.method = endpointMethodUpper;
        }
        // Fix endpoint.protocol inside the step
        if (fixedStep.endpoint) {
          const proto = fixedStep.endpoint.protocol || '';
          if (!proto || /HTTP_METHOD_CONFIG/i.test(proto)) {
            fixedStep.endpoint = { ...fixedStep.endpoint, protocol: expectedProtocol };
          }
        }
        return fixedStep;
      });

      return { ...test, name: fixedName, steps: fixedSteps };
    });
  }

    // Generate data sources from CSV files only (excludes sample JSONs)
    const dataSources = this.generateDataSources(
      generatedFiles, 
      projectId, 
      projectName, 
      folderName
    );

    console.log('🔧 GENERATED DATA SOURCES FOR CONFIG:', dataSources.length);

    // Update each test with data sources and populate fieldsToExtract
    if (populatedConfig.tests && Array.isArray(populatedConfig.tests)) {
      populatedConfig.tests = populatedConfig.tests.map(test => {
        // Add data sources to each test
        const updatedTest = {
          ...test,
          dataSources: dataSources
        };
        
        // Process each step to populate fieldsToExtract from assertions
        if (updatedTest.steps && Array.isArray(updatedTest.steps)) {
          updatedTest.steps = updatedTest.steps.map(step => {
            // Populate fieldsToExtract based on assertions at step level
            console.log('🔧 PROCESSING STEP ASSERTIONS:', step.assertions);
            const populatedFieldsFromAssertions = JsonConfigPopulator.populateFieldsToExtract(step.assertions || []);
            // Merge existing fieldsToExtract from payload with populated ones from assertions
            step.fieldsToExtract = {
              ...step.fieldsToExtract, // Preserve existing fieldsToExtract from payload
              ...populatedFieldsFromAssertions  // Add/override with fields from assertions
            };
            console.log('🔧 MERGED FIELDS TO EXTRACT AT STEP LEVEL:', step.fieldsToExtract);

            // Also populate fieldsToExtract in each request within the requests array
            if (step.requests && Array.isArray(step.requests)) {
              console.log(`🔧 Found ${step.requests.length} request(s) in step`);
              step.requests = step.requests.map((request, reqIndex) => {
                console.log(`🔧 PROCESSING REQUEST ${reqIndex} ASSERTIONS:`, request.assertions);
                const populatedFieldsToExtract = JsonConfigPopulator.populateFieldsToExtract(request.assertions || []);
                console.log(`🔧 POPULATED FIELDS TO EXTRACT IN REQUEST ${reqIndex}:`, populatedFieldsToExtract);

                // Merge existing fieldsToExtract from payload with populated ones from assertions
                // This preserves fieldsToExtract that were already in the payload
                const mergedFieldsToExtract = {
                  ...request.fieldsToExtract, // Preserve existing fieldsToExtract from payload
                  ...populatedFieldsToExtract  // Add/override with fields from assertions
                };
                console.log(`🔧 MERGED FIELDS TO EXTRACT IN REQUEST ${reqIndex}:`, mergedFieldsToExtract);

                // Return a new request object with merged fieldsToExtract
                return {
                  ...request,
                  fieldsToExtract: mergedFieldsToExtract
                };
              });
            }

            return step;
          });
        }
        
        console.log('🔧 ADDED DATA SOURCES TO TEST:', updatedTest.name || 'unnamed', dataSources.length);
        return updatedTest;
      });
    }

    // Update sample content s3Links to match actual generated JSON files
    if (populatedConfig.tests && Array.isArray(populatedConfig.tests)) {
      populatedConfig.tests = populatedConfig.tests.map(test => {
        if (test.steps && Array.isArray(test.steps)) {
          test.steps = test.steps.map(step => {
            // Update sample s3Links for request content
            if (step.sampleRequestContent) {
              if (step.sampleRequestContent.name) {
                step.sampleRequestContent.name = sanitizeName(step.sampleRequestContent.name);
              }
              const requestJsonFile = generatedFiles.find(file =>
                file.filename.toLowerCase().includes('sample_request') &&
                file.filename.toLowerCase().endsWith('.json')
              );
              if (requestJsonFile) {
                step.sampleRequestContent.s3Link = `projects/${projectId}/${projectName}/Automation Scripts/${folderName}/${requestJsonFile.filename}`;
                console.log('🔧 UPDATED REQUEST S3LINK:', step.sampleRequestContent.s3Link);
              } else if (step.sampleRequestContent.s3Link && step.sampleRequestContent.s3Link.includes('{projectId}')) {
                // If we have a template s3Link, extract the filename and rebuild the path correctly
                const filename = step.sampleRequestContent.s3Link.split('/').pop();
                step.sampleRequestContent.s3Link = `projects/${projectId}/${projectName}/Automation Scripts/${folderName}/${filename}`;
                console.log('🔧 UPDATED REQUEST S3LINK FROM TEMPLATE:', step.sampleRequestContent.s3Link);
              }
            }

            // Update sample s3Links for response content
            if (step.sampleResponseContent) {
              if (step.sampleResponseContent.name) {
                step.sampleResponseContent.name = sanitizeName(step.sampleResponseContent.name);
              }
              const responseJsonFile = generatedFiles.find(file =>
                file.filename.toLowerCase().includes('sample_response') &&
                file.filename.toLowerCase().endsWith('.json')
              );
              if (responseJsonFile) {
                step.sampleResponseContent.s3Link = `projects/${projectId}/${projectName}/Automation Scripts/${folderName}/${responseJsonFile.filename}`;
                console.log('🔧 UPDATED RESPONSE S3LINK:', step.sampleResponseContent.s3Link);
              } else if (step.sampleResponseContent.s3Link && step.sampleResponseContent.s3Link.includes('{projectId}')) {
                // If we have a template s3Link, extract the filename and rebuild the path correctly
                const filename = step.sampleResponseContent.s3Link.split('/').pop();
                step.sampleResponseContent.s3Link = `projects/${projectId}/${projectName}/Automation Scripts/${folderName}/${filename}`;
                console.log('🔧 UPDATED RESPONSE S3LINK FROM TEMPLATE:', step.sampleResponseContent.s3Link);
              }
            }

            // Update sample s3Links for requests array (if it exists)
            if (step.requests && Array.isArray(step.requests)) {
              step.requests = step.requests.map(request => {
                // Update request content s3Link - ALWAYS replace if it exists
                if (request.sampleRequestContent) {
                  if (request.sampleRequestContent.name) {
                    request.sampleRequestContent.name = sanitizeName(request.sampleRequestContent.name);
                  }
                  if (request.sampleRequestContent.s3Link) {
                    const filename = request.sampleRequestContent.s3Link.split('/').pop();
                    request.sampleRequestContent.s3Link = `projects/${projectId}/${projectName}/Automation Scripts/${folderName}/${filename}`;
                    console.log('🔧 UPDATED REQUEST S3LINK IN REQUESTS ARRAY:', request.sampleRequestContent.s3Link);
                  }
                }

                // Update response content s3Link - ALWAYS replace if it exists
                if (request.sampleResponseContent) {
                  if (request.sampleResponseContent.name) {
                    request.sampleResponseContent.name = sanitizeName(request.sampleResponseContent.name);
                  }
                  if (request.sampleResponseContent.s3Link) {
                    const filename = request.sampleResponseContent.s3Link.split('/').pop();
                    request.sampleResponseContent.s3Link = `projects/${projectId}/${projectName}/Automation Scripts/${folderName}/${filename}`;
                    console.log('🔧 UPDATED RESPONSE S3LINK IN REQUESTS ARRAY:', request.sampleResponseContent.s3Link);
                  }
                }

                return request;
              });
            }

            return step;
          });
        }
        return test;
      });
    }

    console.log('✅ CONFIG POPULATED WITH DATA SOURCES AND FIELDS TO EXTRACT');

    // DEBUG: Log the final state of fieldsToExtract in requests
    if (populatedConfig.tests && populatedConfig.tests[0] && populatedConfig.tests[0].steps) {
      populatedConfig.tests[0].steps.forEach((step, stepIndex) => {
        if (step.requests) {
          step.requests.forEach((request, reqIndex) => {
            console.log(`🎯 FINAL CHECK - Step ${stepIndex}, Request ${reqIndex} fieldsToExtract:`, request.fieldsToExtract);
          });
        }
      });
    }

    return populatedConfig;
  }

  /**
   * Populates fieldsToExtract object based on assertions array
   * @param {Array} assertions - Array of assertion objects
   * @returns {Object} - fieldsToExtract object with field mappings
   */
  static populateFieldsToExtract(assertions) {
    const fieldsToExtract = {};

    if (!assertions || !Array.isArray(assertions)) {
      console.log('⚠️ No assertions provided or not an array');
      return fieldsToExtract;
    }

    console.log(`📋 Processing ${assertions.length} assertions for fieldsToExtract`);

    assertions.forEach(assertion => {
      if (assertion.key) {
        // Extract the field name from the key (e.g., "response.status" -> "status")
        const fieldName = this.extractFieldName(assertion.key);

        // Map the field name to the full assertion key
        fieldsToExtract[fieldName] = assertion.key;
        console.log(`  ✅ Mapped: ${fieldName} -> ${assertion.key}`);
      }
    });

    console.log('🎯 Final fieldsToExtract:', fieldsToExtract);
    return fieldsToExtract;
  }

  /**
   * Extracts the field name from an assertion key
   * @param {String} key - The assertion key (e.g., "response.status", "data.user.id")
   * @returns {String} - The extracted field name (e.g., "status", "id")
   */
  static extractFieldName(key) {
    if (!key || typeof key !== 'string') {
      return key;
    }

    // Split by dots and take the last part as the field name
    const parts = key.split('.');
    return parts[parts.length - 1];
  }

  /**
   * Generates data sources array from generated files
   * @param {Array} generatedFiles - Array of generated files
   * @param {String} projectId - Project ID
   * @param {String} projectName - Project name
   * @param {String} folderName - Endpoint folder name
   * @returns {Array} - Array of data source objects
   */
static generateDataSources(generatedFiles, projectId, projectName, folderName) {
  console.log('🔍 DEBUG: About to generate data sources');
  console.log('Generated files:', generatedFiles.map(f => f.filename));
  console.log('Project ID:', projectId);
  console.log('Endpoint folder name:', folderName);

  const dataSources = [];
  let dataSourceId = 1;

  // Filter files that should be data sources - ONLY CSV files, exclude sample files
  const dataFiles = generatedFiles.filter(file => {
    const fileName = file.filename.toLowerCase();
    return fileName.endsWith('.csv') && 
           !fileName.includes('sample') &&
           (fileName.includes('request_data') || fileName.includes('response_data'));
  });

  console.log('🔍 FILTERED DATA FILES:', dataFiles.map(f => f.filename));

  // Create a map to find corresponding response files for request files
  const responseFileMap = new Map();
  
  dataFiles.forEach(file => {
    const fileName = file.filename.toLowerCase();
    if (fileName.includes('response') && fileName.includes('data')) {
      // Extract the base name (everything before '_response_data')
      const baseName = file.filename.replace(/_response_data\.csv$/i, '');
      responseFileMap.set(baseName, file.filename.replace(/\.(csv)$/i, ''));
    }
  });

  // Process each data file
  dataFiles.forEach(file => {
    // Determine if this should be primary (only request data files)
    const isPrimary = file.filename.toLowerCase().includes('request') && 
                     file.filename.toLowerCase().includes('data');

    // Generate the S3 content path — same folder the CSVs are uploaded to, so the
    // engine can resolve `content` verbatim (S3 keys are case-sensitive).
    const contentPath = `${buildScriptProjectPath(projectId, projectName, folderName)}/${file.filename}`;

    // Find corresponding response data source name if this is a request file
    let responseDataSourceName = "";
    if (isPrimary) {
      responseDataSourceName = "ExpectedData"; // Default name
    }

    // Create data source object
    const dataSource = {
      id: dataSourceId.toString(),
      name: this.getDataSourceName(file.filename),
      responseDataSource: responseDataSourceName,
      primary: isPrimary,
      type: this.getFileType(file.filename),
      content: contentPath
    };

    console.log('✅ CREATED DATA SOURCE:', dataSource);
    dataSources.push(dataSource);
    dataSourceId++;
  });

  console.log('📊 TOTAL DATA SOURCES GENERATED:', dataSources.length);
  return dataSources;
}

  /**
   * Generates a clean data source name from filename
   * @param {String} filename - Original filename
   * @returns {String} - Clean data source name
   */
  static getDataSourceName(filename) {
    // Check if it's a request or response data file and return appropriate name
    const name = filename.toLowerCase();
    if (name.includes('request') && name.includes('data')) {
      return 'RequestData';
    } else if (name.includes('response') && name.includes('data')) {
      return 'ExpectedData';
    }
    // Fallback: Remove file extension and clean up the name
    return filename.replace(/\.(csv|json|txt)$/i, '');
  }

  /**
   * Determines file type from filename
   * @param {String} filename - Filename
   * @returns {String} - File type (csv, json, etc.)
   */
  static getFileType(filename) {
    const extension = filename.split('.').pop().toLowerCase();
    return extension === 'csv' ? 'csv' : 'json'; // Default to json if not csv
  }

  /**
   * Creates the request_payload.json file content
   * @param {Object} jsonConfig - Populated JSON configuration
   * @returns {Object} - File object ready for upload
   */
  static createRequestPayloadFile(jsonConfig) {
    const content = JSON.stringify(jsonConfig, null, 2);
    return {
      filename: 'request_payload.json',
      content: content,
      format: 'json',
      type: 'config',
      size: content.length,
      // Add base64 encoding for consistency with other files
      base64Content: btoa(content)
    };
  }

  /**
   * Processes multiple JSON configs for multiple endpoints
   * @param {Array} jsonConfigs - Array of JSON configuration objects
   * @param {Object} projectData - Created project data
   * @param {Object} userData - User data
   * @param {Array} endpoints - Array of endpoint objects
   * @param {Array} allGeneratedFiles - All generated files
   * @returns {Array} - Array of populated request_payload files
   */
  static processMultipleConfigs(jsonConfigs, projectData, userData, endpoints, allGeneratedFiles) {
    const requestPayloadFiles = [];

    jsonConfigs.forEach((config, index) => {
      if (endpoints[index]) {
        const endpoint = endpoints[index];
        
        // Filter files for this specific endpoint
        const endpointFiles = this.filterFilesForEndpoint(allGeneratedFiles, endpoint);
        
        // Populate the config
        const populatedConfig = this.populateConfig(
          config, 
          projectData, 
          userData, 
          endpoint, 
          endpointFiles
        );

        // Create the request_payload file
        const requestPayloadFile = this.createRequestPayloadFile(populatedConfig);
        requestPayloadFiles.push(requestPayloadFile);
      }
    });

    return requestPayloadFiles;
  }

  /**
   * Filters generated files for a specific endpoint
   * @param {Array} allFiles - All generated files
   * @param {Object} endpoint - Specific endpoint
   * @returns {Array} - Files related to this endpoint
   */
  static filterFilesForEndpoint(allFiles, endpoint) {
    const endpointMethod = endpoint.method.toLowerCase();
    const endpointPathParts = endpoint.path.split('/').filter(part => part && !part.startsWith('{'));

    return allFiles.filter(file => {
      const fileName = file.filename.toLowerCase();
      
      // Check if filename contains the method
      if (fileName.includes(endpointMethod)) {
        return true;
      }

      // Check if filename contains path parts
      return endpointPathParts.some(part => fileName.includes(part.toLowerCase()));
    });
  }

  /**
   * Updates sample content s3Links to match the correct project path structure
   * @param {Object} populatedConfig - The populated configuration object
   * @param {String} projectId - Project ID
   * @param {String} projectName - Project name
   * @param {String} folderName - Endpoint folder name
   * @returns {Object} - Config with corrected s3Links
   */
  static updateSampleContentPaths(populatedConfig, projectId, projectName, folderName) {
    if (populatedConfig.tests && Array.isArray(populatedConfig.tests)) {
      populatedConfig.tests = populatedConfig.tests.map(test => {
        if (test.steps && Array.isArray(test.steps)) {
          test.steps = test.steps.map(step => {
            // Update sampleRequestContent s3Link
            if (step.sampleRequestContent && step.sampleRequestContent.s3Link) {
              step.sampleRequestContent.name = sanitizeName(step.sampleRequestContent.name);
              const filename = step.sampleRequestContent.name + '.json';
              step.sampleRequestContent.s3Link = `projects/${projectId}/${projectName}/Automation Scripts/${folderName}/${filename}`;
            }

            // Update sampleResponseContent s3Link
            if (step.sampleResponseContent && step.sampleResponseContent.s3Link) {
              step.sampleResponseContent.name = sanitizeName(step.sampleResponseContent.name);
              const filename = step.sampleResponseContent.name + '.json';
              step.sampleResponseContent.s3Link = `projects/${projectId}/${projectName}/Automation Scripts/${folderName}/${filename}`;
            }
            
            return step;
          });
        }
        return test;
      });
    }
    
    return populatedConfig;
  }
}

export default JsonConfigPopulator;